import React, { useEffect, useState, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Tooltip as LeafletTooltip, useMapEvents, Popup, GeoJSON } from 'react-leaflet';
import { Icon, DivIcon } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { LocationData, ParameterSeries } from '../types';
import { toParameterSeries, fetchNearestGridSeries } from '../utils/indiaGrid';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartJsTooltip,
  Legend as ChartLegend
} from 'chart.js';
import L from 'leaflet';
import MiniSparkline from './MiniSparkline';
import CityDualAxesChart from './CityDualAxesChart';
import RainOverlay from './RainOverlay';
import ContourOverlay from './ContourOverlay';
import IndiaBorderMask from './IndiaBorderMask';
import MapBackgroundDimmer from './MapBackgroundDimmer';

// Fix for Leaflet default marker icons
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// Fix for marker icons
delete (Icon.Default.prototype as any)._getIconUrl;
Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartJsTooltip,
  ChartLegend
);

type PolygonClippingPolygon = number[][][];
type PolygonClippingMultiPolygon = PolygonClippingPolygon[];

function simplifyRing(ring: number[][], tolerance: number): number[][] {
  if (tolerance <= 0 || ring.length <= 4) {
    return ring.slice();
  }
  const simplified: number[][] = [];
  const sqTol = tolerance * tolerance;
  let lastKept = ring[0];
  simplified.push([lastKept[0], lastKept[1]]);
  for (let i = 1; i < ring.length - 1; i++) {
    const point = ring[i];
    const dx = point[0] - lastKept[0];
    const dy = point[1] - lastKept[1];
    if ((dx * dx + dy * dy) >= sqTol) {
      simplified.push([point[0], point[1]]);
      lastKept = point;
    }
  }
  const last = ring[ring.length - 1];
  if (simplified[simplified.length - 1][0] !== last[0] || simplified[simplified.length - 1][1] !== last[1]) {
    simplified.push([last[0], last[1]]);
  }
  if (simplified.length < 4) {
    return ring.slice();
  }
  const first = simplified[0];
  const end = simplified[simplified.length - 1];
  if (first[0] !== end[0] || first[1] !== end[1]) {
    simplified.push([...first]);
  }
  return simplified;
}

function toPolygonClippingMultiPolygon(geoJson: any, tolerance = 0): PolygonClippingMultiPolygon | null {
  if (!geoJson) return null;
  const result: PolygonClippingMultiPolygon = [];
  const features = geoJson.type === 'FeatureCollection' ? geoJson.features : [geoJson];
  for (const feature of features) {
    const geometry = feature?.geometry ?? feature;
    if (!geometry) continue;
    if (geometry.type === 'Polygon' && Array.isArray(geometry.coordinates)) {
      const simplified = (geometry.coordinates as number[][][]).map(ring => simplifyRing(ring, tolerance));
      result.push(simplified);
    } else if (geometry.type === 'MultiPolygon' && Array.isArray(geometry.coordinates)) {
      geometry.coordinates.forEach((polygon: PolygonClippingPolygon) => {
        if (Array.isArray(polygon)) {
          const simplifiedPolygon = polygon.map(ring => simplifyRing(ring, tolerance));
          result.push(simplifiedPolygon);
        }
      });
    }
  }
  return result.length ? result : null;
}

interface MeteoMapProps {
  locations: LocationData[];
  selected: { city: string; param: string } | null;
  setSelected: (sel: { city: string; param: string } | null) => void;
  modal: { city: string } | null;
  setModal: (modal: { city: string } | null) => void;
  mapRef: React.RefObject<L.Map>;
  filterState?: {
    filters: Record<string, any>;
    activeFilters: string[];
  };
  // Notify parent (App) whether the SM index (color bar) is visible
  onIndexVisibilityChange?: (visible: boolean) => void;
  // Optional: allow parent (App) to own dynamic locations for sidebar/compare
  onAddLocation?: (loc: LocationData) => void;
  onOpenComparisonSidebar?: () => void;
  onShowToast?: (message: string, type?: 'info' | 'success' | 'error') => void;
}

const createCustomIcon = (color: string, highlight: boolean = false) => {
  return new DivIcon({
    className: 'custom-div-icon',
  html: `<div style="background-color: ${color}; width: 16px; height: 16px; border-radius: 50%; border: 2px solid ${highlight ? '#FFBF00' : '#1E0C0D'}; box-shadow: ${highlight ? '0 0 0 3px rgba(255,191,0,0.35)' : 'none'};"></div>`,
    iconSize: [20, 20],
    iconAnchor: [10, 10],
    popupAnchor: [0, -10]
  });
};

const getDefaultColor = () => '#941C30';

// Generate blue-yellow-red CSS gradient for contour legend
const generateBlueYellowRedGradient = () => {
  const steps = 20;
  const gradientStops: string[] = [];
  
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    let r, g, b;
    
    if (t <= 0.5) {
      // Dark blue to bright yellow
      const localT = t * 2;
      r = Math.round(0 + localT * 255);
      g = Math.round(0 + localT * 255);
      b = Math.round(255 - localT * 255);
    } else {
      // Bright yellow to dark red
      const localT = (t - 0.5) * 2;
      r = Math.round(255 - localT * 70);
      g = Math.round(255 - localT * 255);
      b = Math.round(0);
    }
    
    const percentage = (i / steps * 100).toFixed(1);
    gradientStops.push(`rgb(${r}, ${g}, ${b}) ${percentage}%`);
  }
  
  return `linear-gradient(90deg, ${gradientStops.join(', ')})`;
};

const glassBoxStyle: React.CSSProperties = {
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  zIndex: 2000,
  background: 'rgba(255,255,255,0.25)',
  borderRadius: 24,
  boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
  backdropFilter: 'blur(16px) saturate(180%)',
  WebkitBackdropFilter: 'blur(16px) saturate(180%)',
  border: '1px solid rgba(255,255,255,0.18)',
  padding: 20,
  minWidth: 260,
  maxWidth: 360,
  color: '#1E0C0D',
  transition: 'all 0.3s cubic-bezier(.4,2,.6,1)',
  };
  
const glassButton: React.CSSProperties = {
  background: 'rgba(255,255,255,0.35)',
  border: '1.5px solid rgba(148,28,48,0.18)',
  borderRadius: 12,
  padding: '10px 20px',
  margin: '8px 0',
  fontWeight: 600,
  fontSize: 17,
  color: '#941C30',
  boxShadow: '0 2px 8px rgba(148,28,48,0.08)',
  cursor: 'pointer',
  transition: 'all 0.18s',
  outline: 'none',
  width: '100%',
  textAlign: 'left',
};

const glassButtonActive: React.CSSProperties = {
  ...glassButton,
  background: 'rgba(148,28,48,0.12)',
  color: '#fff',
  border: '1.5px solid #941C30',
};

const iconButton: React.CSSProperties = {
  background: 'rgba(255,255,255,0.25)',
  border: 'none',
  borderRadius: 8,
  fontSize: 20,
  color: '#941C30',
  marginLeft: 8,
  marginRight: 8,
  cursor: 'pointer',
  padding: 6,
  transition: 'background 0.18s',
};

// Continuous SM color scale (0.00 → 0.30+)
const smStops = [
  { v: 0.0, c: '#e0f7e9' },
  { v: 0.1, c: '#7ed957' },
  { v: 0.2, c: '#2ecc40' },
  { v: 0.3, c: '#145a32' },
];
const hexToRgb = (hex: string) => {
  const h = hex.replace('#', '');
  const bigint = parseInt(h.length === 3 ? h.split('').map(ch => ch + ch).join('') : h, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return { r, g, b };
};
const relativeLuminance = ({ r, g, b }: { r: number; g: number; b: number }) => {
  const srgb = [r, g, b].map(v => v / 255).map(c => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)));
  return 0.2126 * srgb[0] + 0.7152 * srgb[1] + 0.0722 * srgb[2];
};
const isColorDark = (hex: string) => relativeLuminance(hexToRgb(hex)) < 0.5;
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const rgbToHex = (r: number, g: number, b: number) =>
  '#' + [r, g, b].map(v => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('');
const getSmColorContinuous = (value: number) => {
  if (!isFinite(value)) return '#7ed957';
  const v = Math.max(0, Math.min(0.3, value));
  let i = 0;
  for (; i < smStops.length - 1; i++) {
    const a = smStops[i], b = smStops[i + 1];
    if (v >= a.v && v <= b.v) {
      const t = (v - a.v) / (b.v - a.v || 1);
      const ca = hexToRgb(a.c), cb = hexToRgb(b.c);
      return rgbToHex(lerp(ca.r, cb.r, t), lerp(ca.g, cb.g, t), lerp(ca.b, cb.b, t));
    }
  }
  // Above last stop
  return smStops[smStops.length - 1].c;
};

// Helper to get color for a value based on filter and legend
const getColorForValue = (filterType: string, value: number): string => {
  if (filterType === 'temperature') {
    if (value < 10) return '#C2B28A';
    if (value < 25) return '#D9B17C';
    if (value < 40) return '#C27D60';
    return '#941C30';
  } else if (filterType === 'pressure') {
    if (value < 1000) return '#C27D60';
    if (value < 1010) return '#D9B17C';
    if (value < 1020) return '#C2B28A';
    return '#F2ECDD';
  } else if (filterType === 'humidity') {
    if (value < 30) return '#941C30';
    if (value < 50) return '#C27D60';
    if (value < 70) return '#D9B17C';
    return '#C2B28A';
  } else if (filterType === 'soilMoisture') {
    if (value < 20) return '#941C30';
    if (value < 40) return '#C27D60';
    if (value < 60) return '#D9B17C';
    return '#C2B28A';
  } else if (filterType === 'surfaceTemperature') {
    if (value < 15) return '#C2B28A';
    if (value < 25) return '#D9B17C';
    if (value < 35) return '#C27D60';
    return '#941C30';
  } else if (filterType === 'precipitation') {
    if (value < 10) return '#b3c6ff';
    if (value < 50) return '#6699ff';
    if (value < 100) return '#3366cc';
    return '#003399';
  } else if (filterType === 'SM' || filterType === 'SMRZ') {
    return getSmColorContinuous(value);
  }
  return '#941C30';
};

// Mapping from filter type to parameter key in data
const FILTER_TYPE_TO_PARAM_KEY: Record<string, string> = {
  temperature: 'T2M',
  pressure: 'PS',
  humidity: 'RH2M',
  soilMoisture: 'SM',
  surfaceTemperature: 'TS',
  precipitation: 'PRECTOT',
  SM: 'SM',
  SMRZ: 'SMRZ',
};

// ECMWF subparameter type
// (ECMWF-specific types removed as they are not used directly here.)

// Type guard for ParameterSeries
function isParameterSeries(param: any): param is ParameterSeries {
  return param && typeof param === 'object' && 'series' in param && 'latest' in param;
}

const MeteoMap: React.FC<MeteoMapProps> = ({ locations, selected, setSelected, modal, setModal, mapRef, filterState, onIndexVisibilityChange, onAddLocation, onOpenComparisonSidebar, onShowToast }) => {
  // Toggles
  // - ENABLE_CONTOUR_CLIPPING: pass India mask to contours for polygon clipping (expensive)
  // - SHOW_INDIA_BORDER: show India outline as a visual border (cheap)
  // - SHOW_INDIA_MASK: add a visual mask to hide outside India (optional; keep off for perf unless needed)
  const ENABLE_CONTOUR_CLIPPING = false;
  const SHOW_INDIA_BORDER = true;
  const SHOW_INDIA_MASK = true;
  const [indiaGeoJSON, setIndiaGeoJSON] = useState<any>(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [zoom, setZoom] = useState<number>(5);
  const [compareOpen, setCompareOpen] = useState<boolean>(false);
  const [compareCities, setCompareCities] = useState<string[]>([]);
  // Ad-hoc dynamic locations created from grid clicks
  const [dynamicLocations, setDynamicLocations] = useState<LocationData[]>([]);
  const [showCityMarkers, setShowCityMarkers] = useState<boolean>(true);
  // Hover state to toggle compact vs expanded mini label
  const [hoveredCity, setHoveredCity] = useState<string | null>(null);
  // Removed compare window; always show full series in compare panel
  // Group/modal navigation state (moved from renderModalContent)
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const [ecmwfMenuOpen, setEcmwfMenuOpen] = useState(false);
  const [selectedEcmwfParam, setSelectedEcmwfParam] = useState<string | null>(null);
  // Pagination state for veg group
  const [vegPage, setVegPage] = useState(0);
  // Pagination state for ECMWF group
  const [ecmwfPage, setEcmwfPage] = useState(0);
  // Map click popup state (loading → ready/error)
  const [clickPopup, setClickPopup] = useState<null | { lat: number; lon: number; stage: 'loading' | 'ready' | 'error'; name?: string; sm?: ParameterSeries; rn?: ParameterSeries; message?: string }>(null);
  // Small cooldown to avoid accidental re-open on pointer release/move
  const [clickCooldownUntil, setClickCooldownUntil] = useState<number>(0);

  // Transition state for animating the mini pill into the menu modal
  const [menuTransition, setMenuTransition] = useState<
    | null
    | {
        active: boolean;
        expanding: boolean;
        from: { left: number; top: number; width: number; height: number };
        city: string;
        finalWidth: number;
        finalHeight: number;
        dx: number; // delta from viewport center to pill center (x)
        dy: number; // delta from viewport center to pill center (y)
        sx: number; // scaleX from pill to final
        sy: number; // scaleY from pill to final
      }
  >(null);

  // SM timeline state (playback/scrub across daily ERA5 series)
  const [smIndex, setSmIndex] = useState<number>(0);
  const [smPlaying, setSmPlaying] = useState<boolean>(false);
  // Removed pulses
  const [smSpeedMs, setSmSpeedMs] = useState<number>(800);
  // Rain overlays (heavy day/week) toggle
  const [showRainDaily, setShowRainDaily] = useState<boolean>(false);
  const [showRainWeek, setShowRainWeek] = useState<boolean>(false);
  const [showSmTiles, setShowSmTiles] = useState<boolean>(false);
  const [showRainTiles, setShowRainTiles] = useState<boolean>(false);
  const [indiaMask, setIndiaMask] = useState<PolygonClippingMultiPolygon | null>(null);
  
  // Contour loading states
  const [contourLoading, setContourLoading] = useState<{
    sm: boolean;
    rain: boolean;
  }>({ sm: false, rain: false });
  
  // Contour data ranges for legend
  const [contourDataRanges, setContourDataRanges] = useState<{
    sm?: { min: number; max: number };
    rain?: { min: number; max: number };
  }>({});
  
  const heavyRain = { day: '2024-08-02', weekStart: '2024-08-01', weekEnd: '2024-08-07' };

  // Handle contour data range updates for legend
  const handleContourDataRangeChange = useCallback((mode: 'sm' | 'rain', minVal: number, maxVal: number) => {
    setContourDataRanges(prev => ({
      ...prev,
      [mode]: { min: minVal, max: maxVal }
    }));
  }, []);

  // Handle contour loading state changes
  const handleContourLoadingChange = useCallback((mode: 'sm' | 'rain', isLoading: boolean) => {
    setContourLoading(prev => ({
      ...prev,
      [mode]: isLoading
    }));
  }, []);

  // Persist SM timeline state across sessions
  useEffect(() => {
    try {
      const saved = localStorage.getItem('smTimeline');
      if (saved) {
        const { index, playing, speed } = JSON.parse(saved);
        if (typeof index === 'number') setSmIndex(index);
        if (typeof playing === 'boolean') setSmPlaying(playing);
        if (typeof speed === 'number') setSmSpeedMs(speed);
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('smTimeline', JSON.stringify({ index: smIndex, playing: smPlaying, speed: smSpeedMs }));
    } catch {}
  }, [smIndex, smPlaying, smSpeedMs]);

  // Note: keyboard shortcuts effect is added after primarySmSeries is declared.


  // Merge static and dynamic locations; if parent manages adds, use only locations
  const allLocations = React.useMemo(() => (onAddLocation ? locations : [...locations, ...dynamicLocations]), [locations, dynamicLocations, onAddLocation]);
  
  // Contour time series based on manifest data (August-October 2024)
  const contourTimeSeries = React.useMemo(() => {
    // Sample manifest time array - this would typically be fetched from the manifest
    const manifestTimes = [
      "2024-08-01", "2024-08-02", "2024-08-03", "2024-08-04", "2024-08-05", "2024-08-06", "2024-08-07", "2024-08-08", "2024-08-09", "2024-08-10",
      "2024-08-11", "2024-08-12", "2024-08-13", "2024-08-14", "2024-08-15", "2024-08-16", "2024-08-17", "2024-08-18", "2024-08-19", "2024-08-20",
      "2024-08-21", "2024-08-22", "2024-08-23", "2024-08-24", "2024-08-25", "2024-08-26", "2024-08-27", "2024-08-28", "2024-08-29", "2024-08-30", "2024-08-31",
      "2024-09-01", "2024-09-02", "2024-09-03", "2024-09-04", "2024-09-05", "2024-09-06", "2024-09-07", "2024-09-08", "2024-09-09", "2024-09-10",
      "2024-09-11", "2024-09-12", "2024-09-13", "2024-09-14", "2024-09-15", "2024-09-16", "2024-09-17", "2024-09-18", "2024-09-19", "2024-09-20",
      "2024-09-21", "2024-09-22", "2024-09-23", "2024-09-24", "2024-09-25", "2024-09-26", "2024-09-27", "2024-09-28", "2024-09-29", "2024-09-30",
      "2024-10-01", "2024-10-02", "2024-10-03", "2024-10-04", "2024-10-05", "2024-10-06", "2024-10-07", "2024-10-08", "2024-10-09", "2024-10-10",
      "2024-10-11", "2024-10-12", "2024-10-13", "2024-10-14", "2024-10-15", "2024-10-16", "2024-10-17", "2024-10-18", "2024-10-19", "2024-10-20",
      "2024-10-21", "2024-10-22", "2024-10-23", "2024-10-24", "2024-10-25", "2024-10-26", "2024-10-27", "2024-10-28", "2024-10-29", "2024-10-30", "2024-10-31"
    ];
    return manifestTimes.map((dateStr, index) => ({ date: dateStr, index }));
  }, []);
  
  // Build canonical SM series to drive the timeline (use selected SM city, else first compare city)
  const primarySmSeries = React.useMemo(() => {
    const candidates: string[] = [];
    if (selected?.param === 'SM' && selected.city) candidates.push(selected.city);
    for (const c of compareCities) if (!candidates.includes(c)) candidates.push(c);
    for (const name of candidates) {
      const loc = allLocations.find(l => l.name === name);
      const ps = loc?.parameters?.['SM'];
      if (isParameterSeries(ps)) return ps.series;
    }
    // fallback: any location with SM
    const anyLoc = allLocations.find(l => isParameterSeries(l.parameters['SM']));
    return anyLoc && isParameterSeries(anyLoc.parameters['SM']) ? (anyLoc.parameters['SM'] as any).series : [];
  }, [allLocations, selected, compareCities]);

  // Keyboard shortcuts for timeline when visible (after primarySmSeries and contourTimeSeries are defined)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Check if contour visualization is active (contour overlays are shown)
      const isContourMode = showSmTiles || showRainTiles;
      const timelineVisible = isContourMode ? contourTimeSeries.length > 0 : 
        (selected?.param === 'SM' || compareOpen) && primarySmSeries.length > 0;
      
      if (!timelineVisible) return;
      const target = e.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON'].includes(target.tagName)) return;
      
      if (e.code === 'Space') {
        e.preventDefault();
        setSmPlaying(p => !p);
      } else if (e.code === 'ArrowRight') {
        e.preventDefault();
        if (isContourMode) {
          setSmIndex(i => Math.min(i + 1, Math.max(0, contourTimeSeries.length - 1)));
        } else {
          setSmIndex(i => Math.min(i + 1, Math.max(0, primarySmSeries.length - 1)));
        }
      } else if (e.code === 'ArrowLeft') {
        e.preventDefault();
        setSmIndex(i => Math.max(i - 1, 0));
      } else if (e.key === '1') {
        setSmSpeedMs(1200);
      } else if (e.key === '2') {
        setSmSpeedMs(800);
      } else if (e.key === '3') {
        setSmSpeedMs(400);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [compareOpen, primarySmSeries.length, contourTimeSeries.length, selected, showSmTiles, showRainTiles]);

  // Keep smIndex in range when series changes
  useEffect(() => {
    const isContourMode = showSmTiles || showRainTiles;
    const maxIndex = isContourMode ? contourTimeSeries.length - 1 : primarySmSeries.length - 1;
    
    if (smIndex > maxIndex) {
      setSmIndex(Math.max(0, maxIndex));
    }
  }, [primarySmSeries, contourTimeSeries, smIndex, showSmTiles, showRainTiles]);

  // Inform parent when the SM index bar visibility changes
  const smIndexBarVisible = (selected?.param === 'SM' || compareOpen);
  useEffect(() => {
    onIndexVisibilityChange?.(smIndexBarVisible);
  }, [smIndexBarVisible, onIndexVisibilityChange]);

  // Playback loop
  useEffect(() => {
    if (!smPlaying) return;
    
    const isContourMode = showSmTiles || showRainTiles;
    const maxIndex = isContourMode ? contourTimeSeries.length - 1 : primarySmSeries.length - 1;
    
    if (maxIndex <= 0) return;
    
    const id = setInterval(() => {
      setSmIndex(i => (i + 1) % (maxIndex + 1));
    }, smSpeedMs);
    return () => clearInterval(id);
  }, [smPlaying, contourTimeSeries.length, primarySmSeries.length, smSpeedMs, showSmTiles, showRainTiles]);

  useEffect(() => {
    let cancelled = false;

    // Only load GeoJSON if needed for either border/mask visuals or for clipping
    if (!ENABLE_CONTOUR_CLIPPING && !SHOW_INDIA_BORDER && !SHOW_INDIA_MASK) {
      setIndiaGeoJSON(null);
      setIndiaMask(null);
      return () => { cancelled = true; };
    }

    const loadIndiaGeoJSON = async () => {
      try {
        const { assetUrl } = await import('../utils/asset');
  const response = await fetch(assetUrl('/in.json'));
        if (!response.ok) {
          throw new Error(`India GeoJSON request failed with status ${response.status}`);
        }
        const data = await response.json();
        if (cancelled) return;
        setIndiaGeoJSON(data);
        // Only compute/persist mask geometry when clipping is enabled
        setIndiaMask(ENABLE_CONTOUR_CLIPPING ? toPolygonClippingMultiPolygon(data, 0.04) : null);
      } catch (error) {
        if (cancelled) return;
        console.error('[MeteoMap] Failed to load India GeoJSON:', error);
        setIndiaGeoJSON(null);
        setIndiaMask(null);
      }
    };

    loadIndiaGeoJSON();

    return () => {
      cancelled = true;
    };
  }, [ENABLE_CONTOUR_CLIPPING, SHOW_INDIA_BORDER, SHOW_INDIA_MASK]);



  // Track map zoom for responsive mini plots
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    setZoom(m.getZoom());
    const onZoom = () => setZoom(m.getZoom());
    m.on('zoomend', onZoom);
    return () => { 
      m.off('zoomend', onZoom); 
    };
  }, [mapRef]);

  // Reset group state when modal/city changes
  useEffect(() => {
    setOpenGroup(null);
    setEcmwfMenuOpen(false);
    setSelectedEcmwfParam(null);
    setVegPage(0);
    setEcmwfPage(0);
  }, [modal, selected]);

  // Do not auto-open compare when selecting SM; compare is user-controlled via ➕/Compare.
  useEffect(() => {
    // intentionally left blank for future hooks on selection
  }, [selected]);

  // On mount, ensure no marker/city is selected and no modal is open
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setSelected(null);
    setModal(null);
    // also clear any click popup remnants
    setClickPopup(null);
    // one-time on mount only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Selected plot data is computed on demand within renderModalContent.

  // Open the city menu (modal) and fly to it – used by marker click and name click
  const openCityMenu = (lat: number, lon: number, city: string) => {
    setSelected(null);
    setModal({ city });
    if (mapRef.current) {
      mapRef.current.flyTo([lat, lon], 9, { animate: true, duration: 1 });
    }
  };

  // Map click: fetch daily series from local ERA5 tiles (no external calls)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleMapClick = useCallback(async (e: L.LeafletMouseEvent) => {
  // Ignore clicks during/just after actions
  if (Date.now() < clickCooldownUntil) return;
  if (clickPopup && clickPopup.stage === 'loading') return;
    const lat = e.latlng.lat;
    const lon = e.latlng.lng;
    // show loading popup at clicked point
  setClickPopup({ lat, lon, stage: 'loading' });
    try {
      // Load nearest-grid point from local tiles
      const grid = await fetchNearestGridSeries(lat, lon);
      if (!grid) {
        setClickPopup({ lat, lon, stage: 'error', message: 'No local ERA5 data here.' });
        onShowToast?.('No local ERA5 data for this point.', 'error');
        return;
      }
      const name = `Point ${lat.toFixed(3)}, ${lon.toFixed(3)}`;
      const smPS = toParameterSeries('SM', grid.time, grid.sm as any);
      const rnPS = toParameterSeries('PRECTOT', grid.time, grid.rain as any);
      const hasSmSeries = Array.isArray((smPS as any).series) && (smPS as any).series.length > 0;
      const hasRnSeries = Array.isArray((rnPS as any).series) && (rnPS as any).series.length > 0;
      if (!hasSmSeries && !hasRnSeries) {
        setClickPopup({ lat, lon, stage: 'error', message: 'No recent data at this location.' });
  onShowToast?.('No recent data at this location.', 'info');
        return;
      }
      // stage ready popup with actions; do not auto-open sidebars/modals
      setClickPopup({ lat, lon, stage: 'ready', name, sm: hasSmSeries ? smPS : undefined, rn: hasRnSeries ? rnPS : undefined });
    } catch (err) {
      setClickPopup({ lat, lon, stage: 'error', message: 'Failed to fetch data.' });
    onShowToast?.('Failed to fetch data. Please try again.', 'error');
    }
  // Dependencies are stable setters/props; we don't need to recreate handler on every render
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clickCooldownUntil, clickPopup]);

  // Centralized helper to add a city to compare with 3-point cap
  const addCityToCompare = useCallback((name: string): 'added' | 'exists' => {
    if (compareCities.includes(name)) {
      setCompareOpen(true);
      onShowToast?.('Already in Compare', 'info');
      return 'exists';
    }
    setCompareCities(prev => [...prev, name]);
    setCompareOpen(true);
    onOpenComparisonSidebar?.();
    onShowToast?.('Added to Compare', 'success');
    return 'added';
  }, [compareCities, onOpenComparisonSidebar, onShowToast]);

  // Map click bound via useMapEvents in JSX below (avoid double-binding)
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    const handler = () => {
      // extend cooldown slightly after any mouseup on the map
      setClickCooldownUntil(prev => Math.max(prev, Date.now() + 200));
    };
    m.on('mouseup', handler as any);
    return () => { m.off('mouseup', handler as any); };
  }, [mapRef]);

  // Trigger a smooth transition from the mini name pill to the modal
  const openCityMenuWithTransition = (
    e: React.MouseEvent,
    lat: number,
    lon: number,
    city: string
  ) => {
    if (menuTransition?.active) return; // avoid overlapping animations
    // Compute starting rect from the clicked element
    const el = e.currentTarget as HTMLElement;
    const rect = el.getBoundingClientRect();
    // Compute final size (responsive) and deltas for transform-based animation (FLIP)
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const finalWidth = Math.min(560, vw - 48);
    const finalHeight = Math.min(440, vh - 96);
    const viewportCenterX = vw / 2;
    const viewportCenterY = vh / 2;
    const srcCenterX = rect.left + rect.width / 2;
    const srcCenterY = rect.top + rect.height / 2;
    const dx = srcCenterX - viewportCenterX;
    const dy = srcCenterY - viewportCenterY;
    const sx = Math.max(0.01, rect.width / finalWidth);
    const sy = Math.max(0.01, rect.height / finalHeight);
  // Start transition overlay
    setMenuTransition({
      active: true,
      expanding: false,
      from: { left: rect.left, top: rect.top, width: rect.width, height: rect.height },
      city,
      finalWidth,
      finalHeight,
      dx,
      dy,
      sx,
      sy,
    });
    // Animate next frame
    requestAnimationFrame(() => {
      setMenuTransition((prev) => (prev ? { ...prev, expanding: true } : prev));
    });
  // Fly to city beneath the overlay
    if (mapRef.current) {
      mapRef.current.flyTo([lat, lon], 9, { animate: true, duration: 1 });
    }
  };

  // Ensure fullscreen is reset when closing modal
  const handleCloseModal = useCallback(() => {
    setFullscreen(false);
    setModal(null);
  }, [setModal]);

  // Render parameter list or plot in the glassy modal
  const renderModalContent = (fullscreen = false,
    openGroup: string | null,
    setOpenGroup: (g: string | null) => void,
    ecmwfMenuOpen: boolean,
    setEcmwfMenuOpen: (b: boolean) => void,
    selectedEcmwfParam: string | null,
    setSelectedEcmwfParam: (p: string | null) => void,
    vegPage: number,
    setVegPage: (p: number) => void,
    ecmwfPage: number,
    setEcmwfPage: (p: number) => void
  ) => {
    if (!modal) return null;
  const loc = allLocations.find(l => l.name === modal.city);
    if (!loc) return null;

    // --- Always show graph if a parameter is selected ---
    if (selected && selected.city === loc.name) {
      const paramObj = loc.parameters[selected.param];
      if (!isParameterSeries(paramObj)) return null;
      const isPrecip = selected.param === 'PRECTOT';
      const stroke = isPrecip ? '#3366cc' : '#941C30';
      const fillBg = isPrecip ? 'rgba(51,102,204,0.2)' : 'rgba(148,28,48,0.2)';
      const pointR = isPrecip ? 1 : 1.5;
      return (
        <>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12, padding: fullscreen ? 24 : 0 }}>
            <button style={iconButton} onClick={() => setSelected(null)} title="Back">⟵</button>
            <h2 style={{ flex: 1, margin: 0 }}>{loc.name} - {paramObj.display} <span style={{ fontWeight: 400, fontSize: 16, color: '#941C30' }}>({paramObj.unit})</span></h2>
            <button style={iconButton} onClick={() => setFullscreen(f => !f)} title="Fullscreen">⛶</button>
            <button style={iconButton} onClick={handleCloseModal} title="Close">×</button>
          </div>
          <div style={fullscreen ? { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' } : {}}>
            <Line
              data={{
                labels: paramObj.series.map((d: { year: number; doy: number }, i: number) => `${d.year}-DOY${d.doy}`),
                datasets: [
                  {
                    label: `${paramObj.display} (${paramObj.unit})`,
                    data: paramObj.series.map((d: { value: number }) => d.value),
                    fill: isPrecip,
                    borderColor: stroke,
                    backgroundColor: fillBg,
                    tension: 0.2,
                    pointRadius: pointR,
                    pointHoverRadius: 4,
                  }
                ]
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { display: false },
                  title: { display: false },
                  tooltip: {
                    enabled: true,
                    mode: 'index',
                    intersect: false,
                    backgroundColor: 'rgba(255,255,255,0.95)',
                    titleColor: '#941C30',
                    bodyColor: '#1E0C0D',
                    borderColor: '#941C30',
                    borderWidth: 1,
                    padding: 12,
                    displayColors: false,
                    callbacks: {
                      title: (items) => {
                        const idx = items[0].dataIndex;
                        const d = paramObj.series[idx];
                        return `Day: ${d.year} (DOY ${d.doy})`;
                      },
                      label: (item) => `Value: ${item.formattedValue} ${paramObj.unit}`
                    }
                  }
                },
                interaction: {
                  mode: 'index',
                  intersect: false
                },
                hover: {
                  mode: 'index',
                  intersect: false
                },
                scales: {
                  x: {
                    title: { display: true, text: 'Date (Year-DOY)' },
                    ticks: { maxTicksLimit: 8 }
                  },
                  y: {
                    title: { display: true, text: paramObj.unit },
                    beginAtZero: isPrecip
                  }
                }
              }}
              height={fullscreen ? undefined : 220}
              width={fullscreen ? undefined : undefined}
              style={fullscreen ? { height: '80vh', width: '90vw' } : {}}
              plugins={[
                {
                  id: 'crosshairLine',
                  afterDraw: (chart) => {
                    const tooltip = chart.tooltip;
                    const activeElements = tooltip && typeof tooltip.getActiveElements === 'function' ? tooltip.getActiveElements() : [];
                    if (activeElements.length) {
                      const ctx = chart.ctx;
                      ctx.save();
                      const activePoint = activeElements[0];
                      ctx.beginPath();
                      ctx.moveTo(activePoint.element.x, chart.chartArea.top);
                      ctx.lineTo(activePoint.element.x, chart.chartArea.bottom);
                      ctx.lineWidth = 1.5;
                      ctx.strokeStyle = 'rgba(148,28,48,0.35)';
                      ctx.setLineDash([4, 4]);
                      ctx.stroke();
                      ctx.setLineDash([]);
                      ctx.restore();
                    }
                  }
                }
              ]}
            />
          </div>
        </>
      );
    }

    // --- Grouping logic ---
    // Define parameter groups
    const NON_REDUNDANT_COMPREHENSIVE_KEYS = [
      'leaf_area_index_low_vegetation',
      'leaf_area_index_high_vegetation',
      'temperature_2m_celsius',
      'total_evaporation_mm_per_day',
      'total_precipitation_mm_per_day',
      'total_ET_mm_per_day',
      'total_leaf_area_index',
    ];
    const GROUPS = [
      {
        key: 'basic',
        label: 'Basic Weather',
        icon: '🌤️',
        params: ['T2M', 'PRECTOT', 'RH2M', 'PS'],
      },
      {
        key: 'soil',
        label: 'Soil Moisture',
        icon: '🌱',
        params: ['SM', 'SMRZ'],
      },
      {
        key: 'veg',
        label: 'Vegetation & Land',
        icon: '🌿',
        params: Object.keys(loc.parameters).filter(
          p => NON_REDUNDANT_COMPREHENSIVE_KEYS.includes(p)
        ),
      },
      {
        key: 'ecmwf',
        label: 'ECMWF Analysis',
        icon: '🛰️',
        params: Object.keys(loc.parameters).filter(p => p.endsWith('_ecmwf')),
      },
    ];
    // Only show groups that have parameters
    const availableGroups = GROUPS.filter(g => g.params.length > 0);

    // --- ECMWF submenu (fixed logic) ---
    if (
      openGroup === 'ecmwf' &&
      (!selectedEcmwfParam || ecmwfMenuOpen)
    ) {
      const ecmwfParams = Object.entries(loc.parameters).filter(([key]) => key.endsWith('_ecmwf'));
      if (ecmwfParams.length === 0) return null;
      // Pagination for ECMWF group if > 5
      const pageSize = 5;
      const paramsToShow = ecmwfParams.length > pageSize
        ? ecmwfParams.slice(ecmwfPage * pageSize, (ecmwfPage + 1) * pageSize)
        : ecmwfParams;
      return (
        <>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
            <button style={iconButton} onClick={() => setOpenGroup(null)} title="Back">⟵</button>
            <h2 style={{ flex: 1, margin: 0 }}>{loc.name} - ECMWF Analysis</h2>
            <button style={iconButton} onClick={handleCloseModal} title="Close">×</button>
          </div>
          <div>
            {paramsToShow.map(([key, paramObj]) => {
              const ps = paramObj as ParameterSeries;
              return (
                <button
                  key={key}
                  style={{
                    ...glassButton,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                  onClick={() => {
                    setSelectedEcmwfParam(key);
                    setSelected({ city: loc.name, param: key });
                  }}
                >
                  <span>
                    {ps.display}: <b>{ps.latest && typeof ps.latest.value === 'number' ? ps.latest.value.toFixed(2) : ''}</b> <span style={{ color: '#941C30', fontWeight: 400 }}>{ps.unit}</span>
                  </span>
                  <span style={{ marginLeft: '8px' }}>📊</span>
                </button>
              );
            })}
            {/* Pagination controls for ECMWF group */}
            {ecmwfParams.length > pageSize && (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16, gap: 8 }}>
                {Array.from({ length: Math.ceil(ecmwfParams.length / pageSize) }).map((_, i) => (
                  <button
                    key={i}
                    style={{
                      ...glassButton,
                      padding: '6px 16px',
                      fontSize: 16,
                      fontWeight: ecmwfPage === i ? 700 : 400,
                      background: ecmwfPage === i ? 'rgba(148,28,48,0.12)' : 'rgba(255,255,255,0.35)',
                      border: ecmwfPage === i ? '1.5px solid #941C30' : '1.5px solid rgba(148,28,48,0.18)',
                      color: ecmwfPage === i ? '#fff' : '#941C30',
                      margin: 0,
                      width: 36,
                      minWidth: 0
                    }}
                    onClick={() => setEcmwfPage(i)}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      );
    }

    // --- If a group is open, show its parameters (with pagination for veg group) ---
    if (openGroup && openGroup !== 'ecmwf') {
      const group = availableGroups.find(g => g.key === openGroup);
      if (!group) return null;
      // If only one parameter, show its graph directly
      if (group.params.length === 1) {
        const param = group.params[0];
        setSelected({ city: loc.name, param });
        setOpenGroup(null);
        return null; // Will re-render with selected
      }
      // Pagination for veg group if > 5
      const pageSize = 5;
      const paramsToShow = group.key === 'veg' && group.params.length > pageSize
        ? group.params.slice(vegPage * pageSize, (vegPage + 1) * pageSize)
        : group.params;
      return (
        <>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
            <button style={iconButton} onClick={() => setOpenGroup(null)} title="Back">⟵</button>
            <h2 style={{ flex: 1, margin: 0 }}>{loc.name} - {group.label}</h2>
            <button style={iconButton} onClick={handleCloseModal} title="Close">×</button>
          </div>
          <div>
            {paramsToShow.map(param => {
              const ps = loc.parameters[param];
              if (!isParameterSeries(ps)) return null;
              return (
                <button
                  key={param}
                  style={{
                    ...(selected && selected.param === param && selected.city === loc.name ? glassButtonActive : glassButton),
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                  onClick={() => {
                    setSelected({ city: loc.name, param });
                  }}
                >
                  <span>
                    {param === 'SM' ? '🌱 ' : ''}{ps.display}: <b>{ps.latest.value}</b> <span style={{ color: '#941C30', fontWeight: 400 }}>{ps.unit}</span>
                  </span>
                  <span style={{ marginLeft: '8px' }}>📊</span>
                </button>
              );
            })}
            {/* Pagination controls for veg group */}
            {group.key === 'veg' && group.params.length > pageSize && (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16, gap: 8 }}>
                {Array.from({ length: Math.ceil(group.params.length / pageSize) }).map((_, i) => (
                  <button
                    key={i}
                    style={{
                      ...glassButton,
                      padding: '6px 16px',
                      fontSize: 16,
                      fontWeight: vegPage === i ? 700 : 400,
                      background: vegPage === i ? 'rgba(148,28,48,0.12)' : 'rgba(255,255,255,0.35)',
                      border: vegPage === i ? '1.5px solid #941C30' : '1.5px solid rgba(148,28,48,0.18)',
                      color: vegPage === i ? '#fff' : '#941C30',
                      margin: 0,
                      width: 36,
                      minWidth: 0
                    }}
                    onClick={() => setVegPage(i)}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      );
    }

    // --- Otherwise, show group buttons ---
    return (
      <>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
          <h2 style={{ flex: 1, margin: 0 }}>{loc.name}</h2>
          <button style={iconButton} onClick={handleCloseModal} title="Close">×</button>
        </div>
        {/* Show combined Soil Moisture + Rainfall chart like the right compare panel */}
        {(() => {
          const smPs = loc.parameters['SM'];
          const rnPs = loc.parameters['PRECTOT'];
          const hasSm = isParameterSeries(smPs) && Array.isArray((smPs as any).series) && (smPs as any).series.length > 0;
          const hasRn = isParameterSeries(rnPs) && Array.isArray((rnPs as any).series) && (rnPs as any).series.length > 0;
          if (!hasSm && !hasRn) return null;
          const smS = hasSm ? (smPs as any).series : [];
          const rS = hasRn ? (rnPs as any).series : [];
          const smU = hasSm ? (smPs as any).unit : '';
          const rU = hasRn ? (rnPs as any).unit : 'mm/day';
          return (
            <div style={{ marginBottom: 12 }}>
              <CityDualAxesChart
                title={`${loc.name} — SM & Rainfall`}
                city={loc.name}
                smSeries={smS}
                smUnit={smU}
                rainSeries={rS}
                rainUnit={rU}
                width={fullscreen ? 680 : 420}
                height={fullscreen ? 320 : 220}
                activeIndex={primarySmSeries.length > 0 ? Math.min(Math.max(0, smIndex), primarySmSeries.length - 1) : undefined}
                clipToActive={true}
              />
            </div>
          );
        })()}
        {/* Quick actions aligned with mini-pill design */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          {(() => {
            const sm = loc.parameters['SM'];
            const hasSM = isParameterSeries(sm);
            const inCompare = compareCities.includes(loc.name);
            return (
              <>
                {hasSM && (
                  <>
                    <button
                      className={`mini-add ${inCompare ? 'active' : ''}`}
                      style={{ padding: '6px 10px', fontWeight: 700 }}
                      onClick={() => {
                        if (inCompare) {
                          setCompareCities(prev => prev.filter(c => c !== loc.name));
                          onShowToast?.('Removed from Compare', 'info');
                          setCompareOpen(prev => {
                            const remaining = compareCities.filter(c => c !== loc.name);
                            return remaining.length > 0 ? prev : false;
                          });
                        } else {
                          addCityToCompare(loc.name);
                        }
                      }}
                      title={inCompare ? 'Remove from compare' : 'Add to compare'}
                    >
                      {inCompare ? '✓ Comparing' : '➕ Compare'}
                    </button>
                    <span className="mini-chip" title="Latest Soil Moisture">
                      {hasSM && typeof (sm as any).latest?.value === 'number'
                        ? `Latest: ${(sm as any).latest.value} ${(sm as any).unit || ''}`
                        : 'No SM data'}
                    </span>
                    <button
                      className="mini-name"
                      style={{ borderRadius: 8 }}
                      title="Open full SM graph"
                      onClick={() => {
                        setSelected({ city: loc.name, param: 'SM' });
                      }}
                    >
                      Open SM graph
                    </button>
                  </>
                )}
              </>
            );
          })()}
        </div>
        <div>
          {availableGroups.map(group => (
            <button
              key={group.key}
              style={{
                ...glassButton,
                fontSize: 18,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                margin: '12px 0',
                padding: '16px 24px',
                boxShadow: '0 2px 12px rgba(148,28,48,0.07)',
                gap: 16,
                overflow: 'hidden',
                position: 'relative',
                minWidth: 0,
                width: '100%',
                maxWidth: 420
              }}
              onClick={() => {
                if (group.key === 'ecmwf') setOpenGroup('ecmwf');
                else setOpenGroup(group.key);
              }}
            >
              <span style={{ fontSize: 26, marginRight: 16, flexShrink: 0 }}>{group.icon}</span>
              <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', overflow: 'hidden' }}>
                <span style={{ fontWeight: 700, fontSize: 18, lineHeight: 1.2, whiteSpace: 'normal', wordBreak: 'break-word' }}>{group.label}</span>
                <span style={{ fontSize: 16, color: '#941C30', fontWeight: 400, marginTop: 2 }}>{group.params.length} {group.params.length === 1 ? 'parameter' : 'parameters'}</span>
              </span>
              <span style={{ fontSize: 22, marginLeft: 16, flexShrink: 0, position: 'relative', right: 0, alignSelf: 'center' }}>➔</span>
            </button>
          ))}
        </div>
      </>
    );
  };
  
  return (
    <>
    <MapContainer 
      center={[23.0, 80.0]} 
      zoom={5}
      minZoom={4} 
      style={{ height: '100%', width: '100%' }}
      className="glass-map"
        ref={mapRef}
    >
      {/* Subtle white dimmer that reduces background clutter when zoomed out */}
      <MapBackgroundDimmer minZoomForNoDim={8} maxDimOpacity={0.6} lowZoomThreshold={3} />
  {(() => { const Clicker: React.FC = () => { useMapEvents({ click: handleMapClick as any }); return null; }; return <Clicker/>; })()}
      {/* Use a styled map tile layer with muted colors */}
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
      />
      {/* Rain grid overlays */}
      {showRainDaily && (
        <RainOverlay mode={{ kind: 'daily', date: heavyRain.day }} visible={true} />
      )}
      {showRainWeek && (
        <RainOverlay mode={{ kind: 'week7', start: heavyRain.weekStart, end: heavyRain.weekEnd }} visible={true} />
      )}
      {/* Colored SM/Rain contour overlays tied to timeline index */}
      <ContourOverlay
        mode="sm"
        dateIndex={(showSmTiles || showRainTiles) ? 
          Math.min(Math.max(0, smIndex), Math.max(0, contourTimeSeries.length - 1)) :
          Math.min(Math.max(0, smIndex), Math.max(0, primarySmSeries.length - 1))
        }
        visible={showSmTiles}
        onDataRangeChange={handleContourDataRangeChange}
        onLoadingChange={handleContourLoadingChange}
  indiaMask={ENABLE_CONTOUR_CLIPPING ? indiaMask : null}
      />
      <ContourOverlay
        mode="rain"
        dateIndex={(showSmTiles || showRainTiles) ? 
          Math.min(Math.max(0, smIndex), Math.max(0, contourTimeSeries.length - 1)) :
          Math.min(Math.max(0, smIndex), Math.max(0, primarySmSeries.length - 1))
        }
        visible={showRainTiles}
        onDataRangeChange={handleContourDataRangeChange}
        onLoadingChange={handleContourLoadingChange}
  indiaMask={ENABLE_CONTOUR_CLIPPING ? indiaMask : null}
      />
      {/* Click popup */}
      {clickPopup && (
        <Popup position={[clickPopup.lat, clickPopup.lon]} eventHandlers={{ remove: () => setClickPopup(null) }}>
          <div
            className={`popup-staged is-${clickPopup.stage}`}
            style={{ minWidth: 220 }}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Loading stage */}
            <div className="popup-section loading">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span className="spinner" />
                <div>
                  <div style={{ fontWeight: 700 }}>Fetching data…</div>
                  <div style={{ fontSize: 12, opacity: 0.8 }}>This may take a moment</div>
                </div>
              </div>
            </div>

            {/* Error stage */}
            <div className="popup-section error">
              <div style={{ color: '#941C30' }}>{clickPopup.message || 'Error'}</div>
            </div>

            {/* Ready stage */}
            <div className="popup-section ready">
              {clickPopup.stage === 'ready' && (
                <div>
                  <div style={{ fontWeight: 700, marginBottom: 8 }}>{clickPopup.name}</div>
                  <div style={{ fontSize: 13, opacity: 0.8, marginBottom: 10 }}>
                    {clickPopup.sm ? 'Soil Moisture available' : 'Soil Moisture unavailable'} • {clickPopup.rn ? 'Rainfall available' : 'Rainfall unavailable'}
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button
                      className="mini-add"
                      onClick={() => {
                        if (!clickPopup) return;
                        const { lat, lon, name, sm, rn } = clickPopup;
                        const loc: LocationData = {
                          name: name!,
                          lat,
                          lon,
                          region: 'India',
                          parameters: { SM: sm as any, PRECTOT: rn as any }
                        } as any;
                        if (onAddLocation) {
                          onAddLocation(loc);
                        } else {
                          setDynamicLocations(prev => {
                            const exists = prev.find(p => Math.abs(p.lat - lat) < 1e-6 && Math.abs(p.lon - lon) < 1e-6);
                            return exists ? prev : [...prev, loc];
                          });
                        }
                        addCityToCompare(name!);
                        setClickPopup(null);
                      }}
                    >
                      Add to Compare
                    </button>
                    <button
                      className="mini-name"
                      onClick={() => {
                        if (!clickPopup) return;
                        const { lat, lon, name, sm, rn } = clickPopup;
                        const loc: LocationData = {
                          name: name!,
                          lat,
                          lon,
                          region: 'India',
                          parameters: { SM: sm as any, PRECTOT: rn as any }
                        } as any;
                        if (onAddLocation) {
                          onAddLocation(loc);
                        } else {
                          setDynamicLocations(prev => {
                            const exists = prev.find(p => Math.abs(p.lat - lat) < 1e-6 && Math.abs(p.lon - lon) < 1e-6);
                            return exists ? prev : [...prev, loc];
                          });
                        }
                        const firstParam = clickPopup.sm ? 'SM' : (clickPopup.rn ? 'PRECTOT' : 'SM');
                        setModal({ city: name! });
                        setSelected({ city: name!, param: firstParam });
                        onShowToast?.('Opened details', 'info');
                        setClickPopup(null);
                      }}
                    >
                      Open Details
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Popup>
      )}
      
      {/* Add India border mask that hides content outside India */}
      {SHOW_INDIA_MASK && indiaGeoJSON && (
        <IndiaBorderMask indiaGeoJSON={indiaGeoJSON} />
      )}
      {/* Add India border outline on top of everything */}
      {SHOW_INDIA_BORDER && indiaGeoJSON && (
        <GeoJSON 
          data={indiaGeoJSON}
          style={{
            color: '#941C30',
            weight: 2,
            opacity: 0.8,
            fillOpacity: 0,
            fillColor: 'transparent'
          }}
        />
      )}
      {/* Top-right conditional legends */}
      {(showSmTiles || showRainTiles) && (() => {
        return (
          <div
            className="top-right-legends glass-panel"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <>
              {showRainTiles && (
                <div className="legend-section">
                  <div className="legend-title">Rainfall Contours (mm/day)</div>
                  {contourLoading.rain ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0' }}>
                      <span className="spinner" style={{ width: 16, height: 16 }} />
                      <span style={{ fontSize: 12, color: '#666' }}>Loading contours...</span>
                    </div>
                  ) : contourDataRanges.rain ? (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span className="legend-label-side">Low</span>
                        <div style={{ height: 12, flex: 1, borderRadius: 6, background: generateBlueYellowRedGradient(), border: '1px solid rgba(148,28,48,0.2)' }} />
                        <span className="legend-label-side">High</span>
                      </div>
                      <div className="legend-labels" style={{ justifyContent: 'space-between' }}>
                        <span>{contourDataRanges.rain.min.toFixed(1)}</span>
                        <span>{((contourDataRanges.rain.min + contourDataRanges.rain.max) / 2).toFixed(1)}</span>
                        <span>{contourDataRanges.rain.max.toFixed(1)}</span>
                      </div>
                    </>
                  ) : null}
                </div>
              )}
              {showSmTiles && (
                <div className="legend-section">
                  <div className="legend-title">Soil Moisture Contours</div>
                  {contourLoading.sm ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0' }}>
                      <span className="spinner" style={{ width: 16, height: 16 }} />
                      <span style={{ fontSize: 12, color: '#666' }}>Loading contours...</span>
                    </div>
                  ) : contourDataRanges.sm ? (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span className="legend-label-side">Dry</span>
                        <div style={{ height: 12, flex: 1, borderRadius: 6, background: generateBlueYellowRedGradient(), border: '1px solid rgba(148,28,48,0.2)' }} />
                        <span className="legend-label-side">Wet</span>
                      </div>
                      <div className="legend-labels" style={{ justifyContent: 'space-between' }}>
                        <span>{contourDataRanges.sm.min.toFixed(3)}</span>
                        <span>{((contourDataRanges.sm.min + contourDataRanges.sm.max) / 2).toFixed(3)}</span>
                        <span>{contourDataRanges.sm.max.toFixed(3)}</span>
                      </div>
                    </>
                  ) : null}
                </div>
              )}
            </>
          </div>
        );
      })()}

      {/* Center-screen loading overlay removed to keep UI unobtrusive; small spinners remain in legends */}
      
  {/* Filter locations based on active filter's min/max */}
  {/* Track placed pill rects (screen space) to avoid overlaps */}
  {(() => { /* scope helper */ return null; })()}
  {(() => { const placedRects: {left:number; top:number; width:number; height:number}[] = []; return (filterState && filterState.activeFilters.length > 0
        ? (() => {
            const filterType = filterState.activeFilters[0];
            const paramKey = FILTER_TYPE_TO_PARAM_KEY[filterType] || filterType;
            const filter = filterState.filters[filterType];
            const min = filter?.minValue;
            const max = filter?.maxValue;
      // Merge dynamic locations for filtering & display
  const allLocs = allLocations;
      return allLocs.filter(loc => {
              const param = loc.parameters[paramKey];
              if (!isParameterSeries(param) || typeof param.latest?.value !== 'number') return false;
              if (min !== null && param.latest.value < min) return false;
              if (max !== null && param.latest.value > max) return false;
              return true;
            });
          })()
  : allLocations
  ).filter(() => showCityMarkers).map((loc, idx) => {
        const position: [number, number] = [loc.lat, loc.lon];
        // Determine color: prioritize Soil Moisture for marker color (current timeline index if active, else latest)
        let color = getDefaultColor();
        const smPs = loc.parameters['SM'];
        const timelineActive = (selected?.param === 'SM' || compareOpen) && primarySmSeries.length > 0;
        if (isParameterSeries(smPs)) {
          const s = (smPs as any).series as { value: number }[];
          const idx = timelineActive ? Math.min(Math.max(0, smIndex), (s?.length || 1) - 1) : -1;
          const val = timelineActive && Array.isArray(s) && s.length > 0
            ? s[idx]?.value
            : (smPs as any).latest?.value;
          color = getColorForValue('SM', typeof val === 'number' ? val : 0);
        } else if (filterState && filterState.activeFilters.length > 0) {
          const filterType = filterState.activeFilters[0];
          const paramKey = FILTER_TYPE_TO_PARAM_KEY[filterType] || filterType;
          const param = loc.parameters[paramKey];
          if (isParameterSeries(param) && param.latest && typeof param.latest.value === 'number') {
            color = getColorForValue(filterType, param.latest.value);
          }
        }
  const isCompared = compareCities.includes(loc.name);
  const customIcon = createCustomIcon(color, isCompared);
        // Adjust label positioning for better alignment
  // Name-only labels removed; titles appear in mini badge
  // Custom positioning remains handled by tooltip direction/offset
        
  // Previous name-only label icon removed in favor of inline tooltip title
        // Determine if this location should show the mini soil moisture plot
        const smParam = loc.parameters['SM'];
        const showMiniPlot = isParameterSeries(smParam);

  // Zoom-aware sizing for compact sparklines
        const getMiniConfig = (z: number) => {
          if (z >= 10) return { width: 140, height: 48, maxPoints: 40 };
          if (z >= 8) return { width: 110, height: 36, maxPoints: 32 };
          if (z >= 6) return { width: 84, height: 28, maxPoints: 24 };
          return { width: 68, height: 24, maxPoints: 18 };
        };
        const miniCfg = getMiniConfig(zoom);

  // Build a label formatter to show relative day numbers when clipping
  const relLabel = (d: { year: number; doy: number }, i: number) => `D${i + 1}`;

        // Smart, per-city tooltip placement with collision-aware vertical nudging
        let tooltipDirection: any =
          loc.name === 'IISc Bangalore' ? 'left' :
          loc.name === 'IIT Tirupati' ? 'right' :
          'top';
        const mapObj = mapRef.current;
        const pt = mapObj ? mapObj.latLngToContainerPoint({ lat: loc.lat, lng: loc.lon } as any) : null;
        // Distance between marker and label (scaled by zoom for readability)
        const markerRadius = 10; // matches custom-div-icon half-size
        const getLabelGaps = (z: number) => {
          if (z >= 10) return { gapX: markerRadius + 30, gapYBase: markerRadius + 16 }; // ~40px, 26px
          if (z >= 8)  return { gapX: markerRadius + 24, gapYBase: markerRadius + 14 }; // ~34px, 24px
          if (z >= 6)  return { gapX: markerRadius + 18, gapYBase: markerRadius + 12 }; // ~28px, 22px
          return { gapX: markerRadius + 16, gapYBase: markerRadius + 10 };             // ~26px, 20px
        };
        const gaps = getLabelGaps(zoom);
        let gapX = gaps.gapX;
        let gapYBase = gaps.gapYBase;
        // Bring label a bit closer for these cities to avoid the “too far” look
        if (loc.name === 'IISc Bangalore' || loc.name === 'IIT Tirupati') {
          // bring even closer for these two cities
          gapX = Math.max(markerRadius + 6, gapX - 18); // reduce ~18px, min ~16px total
        }
  const estWidth = miniCfg.width + 120; // approximate pill width
  const estHeight = miniCfg.height + 12; // approximate pill height
  // Initial small vertical stagger to avoid immediate overlaps
  let yAdjust = tooltipDirection === 'right' ? -6 : (tooltipDirection === 'left' ? 6 : 6);
  if (loc.name === 'IIT Kanpur') yAdjust += 6; // lower Kanpur a bit more
        if (pt) {
          // Predict initial rect
          const rectFor = (dir: string, yOff: number) => {
            if (dir === 'right') {
              return { left: pt.x + gapX, top: pt.y - estHeight/2 + yOff, width: estWidth, height: estHeight };
            } else if (dir === 'left') {
              return { left: pt.x - gapX - estWidth, top: pt.y - estHeight/2 + yOff, width: estWidth, height: estHeight };
            } else { // top
              return { left: pt.x - estWidth/2, top: pt.y - gapYBase - estHeight + yOff, width: estWidth, height: estHeight };
            }
          };
          const intersects = (a: any, b: any) => !(a.left + a.width < b.left || b.left + b.width < a.left || a.top + a.height < b.top || b.top + b.height < a.top);
          // Avoid overlapping previously placed pills
          let tries = 0;
          let alt = 1;
          let rect = rectFor(tooltipDirection, yAdjust);
          const hasOverlap = (rc: any) => {
            for (let k = 0; k < placedRects.length; k++) {
              if (intersects(rc, placedRects[k])) return true;
            }
            return false;
          };
          const stepY = 10;
          const maxShift = 24;
          while (tries < 6 && hasOverlap(rect)) {
            yAdjust += alt * stepY;
            if (yAdjust > maxShift) yAdjust = maxShift;
            if (yAdjust < -maxShift) yAdjust = -maxShift;
            alt *= -1;
            tries++;
            rect = rectFor(tooltipDirection, yAdjust);
          }
          placedRects.push(rect);
        }
        // Align the visual tooltip offset with our collision model
        const tooltipOffset: [number, number] =
          tooltipDirection === 'right' ? [gapX, yAdjust] :
          tooltipDirection === 'left' ? [-gapX, yAdjust] :
          [0, -gapYBase + yAdjust];

        return (
          <React.Fragment key={loc.name + '-frag'}>
            {/* Removed previous name-only markers; name appears inside tooltip badge */}
            <Marker
              key={loc.name}
              position={position}
              icon={customIcon}
              eventHandlers={{
                click: () => openCityMenu(loc.lat, loc.lon, loc.name),
                mouseover: () => setHoveredCity(loc.name),
                mouseout: () => setHoveredCity((c) => (c === loc.name ? null : c)),
              }}
            >
        {showMiniPlot && zoom >= 5 && (isCompared || hoveredCity === loc.name) && (
                <LeafletTooltip
                  permanent
                  direction={tooltipDirection}
                  offset={tooltipOffset}
                  opacity={1}
                  className="mini-tooltip"
                >
                  {(() => {
                    const isHovered = hoveredCity === loc.name;
                    const s = (smParam as any).series as { value: number }[];
                    const val = timelineActive && Array.isArray(s) && s.length > 0
                      ? s[Math.min(Math.max(0, smIndex), s.length - 1)]?.value
                      : (smParam as any).latest?.value;
                    const pillColor = getColorForValue('SM', typeof val === 'number' ? val : 0);
                    const dark = isColorDark(pillColor);
                    const fg = dark ? '#ffffff' : '#1E0C0D';
                    if (!isHovered) {
                      // Compact label (always visible, minimal)
                      return (
                        <div
                          className="mini-pill compact"
                          onMouseDown={(e) => e.stopPropagation()}
                          onMouseEnter={() => setHoveredCity(loc.name)}
                          onMouseLeave={() => setHoveredCity((c) => (c === loc.name ? null : c))}
                          title={`${loc.name}: ${typeof val === 'number' ? val.toFixed(3) : '-'}`}
                          style={{ border: `1px solid ${pillColor}`, background: pillColor, color: fg }}
                        >
                          <span style={{ width: 8, height: 8, borderRadius: 4, background: fg, opacity: 0.9, display: 'inline-block' }} />
                          <span className="mini-name" style={{ cursor: 'default', color: fg, background: 'transparent', borderColor: fg }}>{loc.name}</span>
                          <span className="mini-chip" style={{ background: dark ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.06)', borderColor: fg, color: fg }}>{typeof val === 'number' ? val.toFixed(3) : '-'}</span>
                        </div>
                      );
                    }
                    // Expanded pill on hover
                    return (
                      <div
                        className="mini-pill"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                        onDoubleClick={(e) => e.stopPropagation()}
                        onMouseLeave={() => setHoveredCity((c) => (c === loc.name ? null : c))}
                        style={{ border: `1px solid ${pillColor}`, background: pillColor, color: fg }}
                      >
                        <button
                          className="mini-name"
                          onClick={(e) => {
                            e.stopPropagation();
                            openCityMenuWithTransition(e, loc.lat, loc.lon, loc.name);
                          }}
                          title={`Open menu for ${loc.name}`}
                          style={{ color: fg, background: 'transparent', borderColor: fg }}
                        >
                          {loc.name} ›
                        </button>
                        <span className="mini-chip" title="Soil Moisture at current time" style={{ borderColor: fg, background: dark ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.06)', color: fg }}>
                          {`🌱 ${typeof val === 'number' ? val.toFixed(3) : '-'}`}
                        </span>
                        <MiniSparkline
                          unit={(smParam as any).unit || ''}
                          // @ts-ignore
                          series={smParam.series}
                          width={miniCfg.width}
                          height={miniCfg.height}
                          color={dark ? '#ffffff' : '#1E0C0D'}
                          maxPoints={miniCfg.maxPoints}
                          showHeader={false}
                          activeIndex={timelineActive ? Math.min(Math.max(0, Math.min(smIndex, smParam.series.length - 1)), miniCfg.maxPoints - 1) : undefined}
                          clipToActive={timelineActive}
                          labelFormatter={timelineActive ? (d, i) => relLabel(d, i) : undefined}
                        />
                        <button
                          className={`mini-add ${isCompared ? 'active' : ''}`}
                          title={isCompared ? 'Already in compare' : 'Add to compare'}
                          onClick={(e) => {
                            e.stopPropagation();
                            addCityToCompare(loc.name);
                          }}
                        >
                          {isCompared ? '✓' : '➕'}
                        </button>
                      </div>
                    );
                  })()}
                </LeafletTooltip>
              )}
            </Marker>
            {/* Pulsing halos removed per request */}
          </React.Fragment>
        );
  })})()}
    </MapContainer>
    {/* Map Overlay Controls */}
    <div
      className="map-controls"
      style={{
        position: 'fixed',
        left: 16,
        bottom: 16,
        zIndex: 2601,
        display: 'flex',
        gap: 8,
        flexDirection: 'column',
        alignItems: 'flex-start',
        pointerEvents: 'auto',
      }}
      onMouseDown={(e) => { e.stopPropagation(); }}
      onClick={(e) => { e.stopPropagation(); }}
      onDoubleClick={(e) => { e.stopPropagation(); }}
      onPointerDown={(e) => { e.stopPropagation(); }}
    >
      <button
        className={`map-control-btn ${showCityMarkers ? 'active' : ''}`}
        onClick={(e) => { e.stopPropagation(); setShowCityMarkers(v => !v); }}
        title={showCityMarkers ? 'Hide city markers/labels' : 'Show city markers/labels'}
      >
        {showCityMarkers ? 'Hide Cities' : 'Show Cities'}
      </button>
      <button
        className={`map-control-btn ${showSmTiles ? 'active' : ''}`}
        onClick={(e) => { e.stopPropagation(); setClickCooldownUntil(Date.now() + 400); setShowSmTiles(v => !v); }}
        disabled={contourLoading.sm}
        title="Toggle Soil Moisture overlay"
      >
        {contourLoading.sm ? (<><span className="spinner" /> Loading SM…</>) : (showSmTiles ? 'Hide Soil Moisture' : 'Show Soil Moisture')}
      </button>
      <button
        className={`map-control-btn ${showRainTiles ? 'active' : ''}`}
        onClick={(e) => { e.stopPropagation(); setClickCooldownUntil(Date.now() + 400); setShowRainTiles(v => !v); }}
        disabled={contourLoading.rain}
        title="Toggle Rainfall overlay"
      >
        {contourLoading.rain ? (<><span className="spinner" /> Loading Rain…</>) : (showRainTiles ? 'Hide Rainfall' : 'Show Rainfall')}
      </button>
      <button
        className={`map-control-btn ${showRainDaily ? 'active' : ''}`}
        onClick={(e) => { e.stopPropagation(); setClickCooldownUntil(Date.now() + 400); setShowRainDaily(v => !v); }}
        title="Highlight heavy rain (day)"
      >
        {showRainDaily ? 'Hide Heavy Day Rain' : 'Show Heavy Day Rain'}
      </button>
      <button
        className={`map-control-btn ${showRainWeek ? 'active' : ''}`}
        onClick={(e) => { e.stopPropagation(); setClickCooldownUntil(Date.now() + 400); setShowRainWeek(v => !v); }}
        title="Highlight heavy rain (7-day)"
      >
        {showRainWeek ? 'Hide Heavy 7-Day Rain' : 'Show Heavy 7-Day Rain'}
      </button>
    </div>
    {/* Rainfall legend (shown when rain tiles visible). Stacks above SM bar if present. */}
    {showRainTiles && (
      <div
        className="map-colorbar glass-panel"
        style={{
          position: 'fixed',
          left: 16,
          /* adjusted to avoid filter button overlap; raised when SM index visible */
          bottom: (selected?.param === 'SM' || compareOpen) ? 300 : 210,
          zIndex: 2004,
          padding: '10px 12px',
          borderRadius: 12,
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 6, color: '#1E0C0D' }}>Rainfall (mm/day)</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div title="< 10" style={{ width: 22, height: 12, borderRadius: 6, background: '#b3c6ff', border: '1px solid rgba(148,28,48,0.2)' }} />
          <div title="10 - 50" style={{ width: 22, height: 12, borderRadius: 6, background: '#6699ff', border: '1px solid rgba(148,28,48,0.2)' }} />
          <div title="50 - 100" style={{ width: 22, height: 12, borderRadius: 6, background: '#3366cc', border: '1px solid rgba(148,28,48,0.2)' }} />
          <div title=">= 100" style={{ width: 22, height: 12, borderRadius: 6, background: '#003399', border: '1px solid rgba(148,28,48,0.2)' }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 11, color: '#555' }}>
          <span>{'<10'}</span>
          <span>{'10-50'}</span>
          <span>{'50-100'}</span>
          <span>{'≥100'}</span>
        </div>
      </div>
    )}
    {/* SM color bar (map overlay) shown when SM is in focus (selected or comparing) */}
    {(selected?.param === 'SM' || compareOpen) && (
      <div className="map-colorbar glass-panel" style={{ position: 'fixed', left: 16, bottom: 240, zIndex: 2001, padding: '10px 12px', borderRadius: 12 }}>
        <div style={{ fontWeight: 600, marginBottom: 6, color: '#1E0C0D' }}>Soil Moisture</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, color: '#555', width: 30, textAlign: 'right' }}>Dry</span>
          <div style={{ height: 12, width: 200, borderRadius: 6, background: 'linear-gradient(90deg, #e0f7e9 0%, #7ed957 33%, #2ecc40 66%, #145a32 100%)', border: '1px solid rgba(148,28,48,0.2)' }} />
          <span style={{ fontSize: 12, color: '#555' }}>Wet</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 11, color: '#555' }}>
          <span>0.00</span>
          <span>0.10</span>
          <span>0.20</span>
          <span>0.30+</span>
        </div>
      </div>
    )}
    {/* Timeline controls: visible when viewing contours or SM comparison */}
    {(((showSmTiles || showRainTiles) && contourTimeSeries.length > 0) || ((selected?.param === 'SM' || compareOpen) && primarySmSeries.length > 0)) && (
      <div style={{ position: 'fixed', left: '50%', transform: 'translateX(-50%)', bottom: 16, zIndex: 2600, background: 'rgba(255,255,255,0.8)', backdropFilter: 'blur(10px)', border: '1px solid rgba(148,28,48,0.2)', borderRadius: 12, boxShadow: '0 6px 18px rgba(31,38,135,0.18)', padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8, minWidth: 320, maxWidth: '70vw' }}>
        <button className="mini-add" onClick={() => setSmPlaying(p => !p)} title={smPlaying ? 'Pause' : 'Play'} style={{ padding: '4px 10px' }}>{smPlaying ? '⏸' : '▶️'}</button>
        {(showSmTiles || showRainTiles) ? (
          <>
            <input type="range" min={0} max={Math.max(0, contourTimeSeries.length - 1)} value={smIndex} onChange={e => setSmIndex(Number(e.target.value))} style={{ flex: 1 }} />
            <span className="mini-chip" style={{ whiteSpace: 'nowrap' }}>
              {contourTimeSeries[smIndex]?.date ? 
                new Date(contourTimeSeries[smIndex].date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 
                'Loading...'
              }
            </span>
          </>
        ) : (
          <>
            <input type="range" min={0} max={Math.max(0, primarySmSeries.length - 1)} value={smIndex} onChange={e => setSmIndex(Number(e.target.value))} style={{ flex: 1 }} />
            <span className="mini-chip" style={{ whiteSpace: 'nowrap' }}>{primarySmSeries[smIndex]?.year}-D{primarySmSeries[smIndex]?.doy}</span>
          </>
        )}
        <select className="mini-name" style={{ padding: '4px 6px' }} value={String(smSpeedMs)} onChange={e => setSmSpeedMs(Number(e.target.value))} title="Playback speed">
          <option value="2000">Very Slow</option>
          <option value="1000">Slow</option>
          <option value="500">Normal</option>
          <option value="250">Fast</option>
          <option value="150">Very Fast</option>
        </select>
      </div>
    )}
    {/* Right-side Soil Moisture compare panel */}
    {compareOpen && (
    <div className="compare-panel glass-panel" style={{ position: 'fixed', top: 64, right: 16, width: 420, maxWidth: '50vw', height: 'calc(100vh - 80px)', zIndex: 2500, padding: 12, borderRadius: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <strong style={{ flex: 1 }}>Compare ({compareCities.length})</strong>
          {compareCities.length > 0 && (
            <button
              className="mini-name"
              title="Clear all comparisons"
              onClick={() => {
                setCompareCities([]);
                setCompareOpen(false);
                onShowToast?.('Cleared all comparisons', 'info');
              }}
              style={{ padding: '4px 8px' }}
            >
              Clear All
            </button>
          )}
          <button style={iconButton} onClick={() => setCompareOpen(false)} title="Close">×</button>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {compareCities.map(c => (
            <span key={c} className="mini-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <button className="mini-name" title={`Open ${c}`} onClick={() => setModal({ city: c })}>{c}</button>
              <button
                className="mini-chip"
                title={`Remove ${c}`}
                onClick={() => setCompareCities(prev => prev.filter(x => x !== c))}
                style={{ background: 'rgba(0,0,0,0.06)' }}
              >
                ✕
              </button>
            </span>
          ))}
        </div>
  {/* Window selector removed to ensure consistent playback/labels */}
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {compareCities.length === 0 && (
            <div className="no-comparisons-message">
              <p>Add locations from the map labels (➕) to see per-city SM vs Rainfall.</p>
            </div>
          )}
          {compareCities.map(city => {
            const loc = allLocations.find(l => l.name === city);
            const smS = loc && isParameterSeries(loc.parameters['SM']) ? (loc.parameters['SM'] as any).series : [];
            const smU = loc && isParameterSeries(loc.parameters['SM']) ? (loc.parameters['SM'] as any).unit : '';
            const rS = loc && isParameterSeries(loc.parameters['PRECTOT']) ? (loc.parameters['PRECTOT'] as any).series : [];
            const rU = loc && isParameterSeries(loc.parameters['PRECTOT']) ? (loc.parameters['PRECTOT'] as any).unit : 'mm/day';
            return (
              <CityDualAxesChart
                key={`city-dual-${city}`}
                title={`${city}`}
                city={city}
                smSeries={smS}
                smUnit={smU}
                rainSeries={rS}
                rainUnit={rU}
                width={380}
                height={240}
                activeIndex={primarySmSeries.length > 0 ? Math.min(Math.max(0, smIndex), primarySmSeries.length - 1) : undefined}
                clipToActive={true}
              />
            );
          })}

          {/* Analytics strip: clearer layout with per-city stats, color-coded correlations, and clickable divergence windows */}
          {compareCities.length > 0 && (() => {
            const seriesObjs = compareCities.map((city, idx) => {
              const loc = locations.find(l => l.name === city);
              const s = loc && isParameterSeries(loc.parameters['SM']) ? (loc.parameters['SM'] as any).series as { year: number; doy: number; value: number }[] : [];
              return { city, s };
            });
            const lengths = seriesObjs.map(o => o.s.length).filter(n => n > 0);
            if (lengths.length === 0) return null;
            const minLen = Math.min(...lengths);
            if (!isFinite(minLen) || minLen <= 1) return null;
            const timeline = seriesObjs[0].s.slice(0, minLen).map(d => ({ year: d.year, doy: d.doy }));
            const arrays = seriesObjs.map(o => o.s.slice(0, minLen).map(d => d.value));

            // Helpers
            const mean = (arr: number[]) => arr.reduce((a, b) => a + b, 0) / arr.length;
            const std = (arr: number[]) => {
              const m = mean(arr);
              const v = arr.reduce((acc, x) => acc + (x - m) * (x - m), 0) / (arr.length || 1);
              return Math.sqrt(v);
            };
            const corr = (a: number[], b: number[]) => {
              const n = Math.min(a.length, b.length);
              if (n <= 1) return NaN;
              const ma = mean(a);
              const mb = mean(b);
              let num = 0, da = 0, db = 0;
              for (let i = 0; i < n; i++) {
                const xa = a[i] - ma;
                const xb = b[i] - mb;
                num += xa * xb;
                da += xa * xa;
                db += xb * xb;
              }
              const den = Math.sqrt(da * db);
              return den === 0 ? NaN : num / den;
            };

            // Colors per city (stable HSL palette)
            const cityColor = (i: number) => `hsl(${Math.round((i * 360) / Math.max(1, seriesObjs.length))}, 70%, 45%)`;

            // Per-city stats
            const stats = arrays.map((arr, i) => ({
              city: seriesObjs[i].city,
              avg: mean(arr),
              sd: std(arr),
              color: cityColor(i),
            }));

            // Pairwise correlations
            const cors: { a: string; b: string; r: number }[] = [];
            for (let i = 0; i < arrays.length; i++) {
              for (let j = i + 1; j < arrays.length; j++) {
                cors.push({ a: seriesObjs[i].city, b: seriesObjs[j].city, r: corr(arrays[i], arrays[j]) });
              }
            }
            const corrColor = (r: number) => (r >= 0.8 ? '#2ecc71' : r >= 0.6 ? '#f1c40f' : '#e67e22');

            // Divergence windows
            const spreads = new Array(minLen).fill(0).map((_, idx) => {
              let mn = Infinity, mx = -Infinity;
              for (const arr of arrays) {
                const v = arr[idx];
                if (v < mn) mn = v;
                if (v > mx) mx = v;
              }
              return { idx, spread: mx - mn };
            });
            const top = [...spreads].sort((a, b) => b.spread - a.spread).slice(0, 3);
            const mergeWindows = (wins: { start: number; end: number; peak: number }[]) => {
              wins.sort((a, b) => a.start - b.start);
              const out: typeof wins = [];
              for (const w of wins) {
                if (!out.length || w.start > out[out.length - 1].end + 1) out.push({ ...w });
                else out[out.length - 1].end = Math.max(out[out.length - 1].end, w.end);
              }
              return out;
            };
            const rawWins = top.map(t => ({ start: Math.max(0, t.idx - 2), end: Math.min(minLen - 1, t.idx + 2), peak: t.idx }));
            const wins = mergeWindows(rawWins).slice(0, 2);
            const winSummaries = wins.map(w => {
              let hiCity = '', loCity = '';
              let hiVal = -Infinity, loVal = Infinity;
              arrays.forEach((arr, i) => {
                const seg = arr.slice(w.start, w.end + 1);
                const m = mean(seg);
                if (m > hiVal) { hiVal = m; hiCity = seriesObjs[i].city; }
                if (m < loVal) { loVal = m; loCity = seriesObjs[i].city; }
              });
              const dStart = timeline[w.start];
              const dEnd = timeline[w.end];
              return { label: `${dStart.year}-D${dStart.doy} – ${dEnd.year}-D${dEnd.doy}`, hiCity, loCity, startIdx: w.start, spread: spreads[w.peak].spread };
            });

            return (
              <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <strong style={{ color: '#941C30' }}>Analytics</strong>
                  <div style={{ height: 1, background: 'rgba(148,28,48,0.15)', flex: 1, marginLeft: 8 }} />
                </div>

                {/* Per-city mean ± sd rows */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {stats.map(s => (
                    <div key={s.city} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ width: 10, height: 10, borderRadius: 6, background: s.color, display: 'inline-block' }} />
                      <span style={{ flex: 1 }}>{s.city}</span>
                      <span className="mini-chip" title={`Mean ± SD`}>
                        μ {s.avg.toFixed(3)} · σ {s.sd.toFixed(3)}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Correlations */}
                {cors.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <span className="mini-name" style={{ padding: '2px 6px' }}>Correlation</span>
                    {cors.map(c => (
                      <div key={`${c.a}-${c.b}`} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ width: 140, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.a.split(' ').pop()}–{c.b.split(' ').pop()}</span>
                        <div style={{ flex: 1, height: 6, background: 'rgba(0,0,0,0.08)', borderRadius: 4, overflow: 'hidden' }}>
                          <div style={{ width: `${Math.max(0, Math.min(100, Math.round(((c.r || 0) + 1) * 50)))}%`, height: '100%', background: corrColor(c.r || 0) }} />
                        </div>
                        <span className="mini-chip" style={{ padding: '2px 6px' }}>{isFinite(c.r) ? c.r.toFixed(2) : '—'}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Divergence windows */}
                {winSummaries.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <span className="mini-name" style={{ padding: '2px 6px' }}>Top divergence</span>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {winSummaries.map((w, i) => (
                        <button key={`win-${i}`} className="mini-chip" title={`Jump to ${w.label}`} onClick={() => setSmIndex(w.startIdx)} style={{ cursor: 'pointer' }}>
                          {w.label}: ↑ {w.hiCity.split(' ').pop()} ↓ {w.loCity.split(' ').pop()} (Δ {w.spread.toFixed(3)}) ↦
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </div>
    )}
  {/* Left live panel removed per request; compare panel sparkline remains live */}
    {/* Animated transition box from name pill to centered menu */}
    {menuTransition && menuTransition.active && (
      <div
        className={`menu-transition-box ${menuTransition.expanding ? 'expand' : ''}`}
        style={{
          position: 'fixed',
          left: '50%',
          top: '50%',
          width: menuTransition.finalWidth,
          height: menuTransition.finalHeight,
          zIndex: 3000,
          transform: menuTransition.expanding
            ? `translate(-50%, -50%) translate(0px, 0px) scale(1, 1)`
            : `translate(-50%, -50%) translate(${menuTransition.dx}px, ${menuTransition.dy}px) scale(${menuTransition.sx}, ${menuTransition.sy})`,
        }}
    onTransitionEnd={() => {
          // When expansion completes, open the modal and clear overlay
          if (menuTransition.expanding) {
      setModal({ city: menuTransition.city });
      setSelected({ city: menuTransition.city, param: 'SM' });
            setMenuTransition(null);
          }
        }}
      >
        <div style={{
          width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 700, color: '#1E0C0D'
        }}>
          {menuTransition.city}
        </div>
      </div>
    )}
  {modal && (
        <div
          className="glass-modal"
          style={{
            ...glassBoxStyle,
            ...(fullscreen ? {
              width: '100vw',
              height: '100vh',
              minWidth: 0,
              maxWidth: 'none',
              padding: 0,
              top: 0,
              left: 0,
              transform: 'none',
              zIndex: 3000
            } : {})
          }}
        >
          <div style={fullscreen ? { width: '100vw', height: '100vh', display: 'flex', flexDirection: 'column' } : {}}>
            {renderModalContent(fullscreen, openGroup, setOpenGroup, ecmwfMenuOpen, setEcmwfMenuOpen, selectedEcmwfParam, setSelectedEcmwfParam, vegPage, setVegPage, ecmwfPage, setEcmwfPage)}
          </div>
        </div>
      )}
    </>
  );
};

export default MeteoMap;